import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Camera,
  ChevronDown,
  ChevronUp,
  Zap,
  Clock,
  TrendingUp,
  Shield,
  Sparkles,
} from "lucide-react";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Badge } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/select";
import { toast } from "sonner";

interface FoodItem {
  id: string;
  name: string;
  servingSize: string;
  calories: number;
  protein: number;
  carbs: number;
  netCarbs: number;
  fat: number;
  fiber: number;
  transFat: number;
  saturatedFat: number;
  sugar: number;
  gi: number;
  gl: number;
}

const MealPrediction = () => {
  const navigate = useNavigate();
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [mealType, setMealType] = useState<string>("breakfast");

  const [foodItems, setFoodItems] = useState<FoodItem[]>([
    {
      id: "1",
      name: "Scrambled Eggs",
      servingSize: "2 large eggs",
      calories: 180,
      protein: 13,
      carbs: 2,
      netCarbs: 2,
      fat: 14,
      fiber: 0,
      transFat: 0.5,
      saturatedFat: 4,
      sugar: 1,
      gi: 0,
      gl: 0,
    },
    {
      id: "2",
      name: "Whole Wheat Toast",
      servingSize: "2 slices",
      calories: 160,
      protein: 8,
      carbs: 28,
      netCarbs: 24,
      fat: 2,
      fiber: 4,
      transFat: 0,
      saturatedFat: 0.5,
      sugar: 4,
      gi: 51,
      gl: 14,
    },
    {
      id: "3",
      name: "Avocado",
      servingSize: "1/2 medium",
      calories: 120,
      protein: 1.5,
      carbs: 6,
      netCarbs: 1,
      fat: 11,
      fiber: 5,
      transFat: 0,
      saturatedFat: 1.5,
      sugar: 0.5,
      gi: 15,
      gl: 1,
    },
  ]);

  const totalNutrition = foodItems.reduce(
    (acc, item) => ({
      calories: acc.calories + item.calories,
      protein: acc.protein + item.protein,
      carbs: acc.carbs + item.carbs,
      fat: acc.fat + item.fat,
      fiber: acc.fiber + item.fiber,
      sugar: acc.sugar + item.sugar,
      gi: acc.gi + item.gi,
      gl: acc.gl + item.gl,
    }),
    {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      fiber: 0,
      sugar: 0,
      gi: 0,
      gl: 0,
    }
  );

  const avgGI =
    foodItems.length > 0 ? Math.round(totalNutrition.gi / foodItems.length) : 0;
  const avgGL =
    foodItems.length > 0 ? Math.round(totalNutrition.gl / foodItems.length) : 0;

  const handleDeleteItem = (id: string) => {
    setFoodItems(foodItems.filter(item => item.id !== id));
    toast.success("Food item removed");
  };

  const handleSaveMeal = () => {
    localStorage.setItem("lastMealTime", new Date().toISOString());
    toast.success(`Meal saved as ${mealType}!`);
    navigate("/meals");
  };

  // Calculate score & insight
  const totalCal = totalNutrition.calories || 1;
  const carbPct = (totalNutrition.carbs * 4) / totalCal;
  const proteinPct = (totalNutrition.protein * 4) / totalCal;
  const fatPct = (totalNutrition.fat * 9) / totalCal;

  let insight = "";
  let score = 75;
  if (carbPct > 0.5 && proteinPct < 0.2 && fatPct < 0.25) {
    insight =
      "Carb-focused meal. Adding protein or healthy fat would improve balance.";
    score = 45;
  } else if (proteinPct > 0.4 && carbPct < 0.2) {
    insight = "High-protein meal. Adding fiber-rich carbs could aid digestion.";
    score = 55;
  } else if (fatPct > 0.5) {
    insight =
      "Fat-heavy meal. Consider adding lean protein or vegetables for balance.";
    score = 40;
  } else if (totalNutrition.fiber < 2 && foodItems.length >= 2) {
    insight =
      "Low fiber content. Adding whole grains or vegetables would help gut health.";
    score = 50;
  } else if (totalNutrition.sugar > 15) {
    insight =
      "High sugar content. Consider swapping sugary items for whole food alternatives.";
    score = 35;
  } else {
    insight =
      "Well-balanced meal with a good mix of macronutrients. Great job!";
    score = 82;
  }
  if (avgGL >= 15) {
    score = Math.max(score - 10, 0);
  } else if (avgGL < 8) {
    score = Math.min(score + 5, 100);
  }

  const scoreLabel =
    score >= 80
      ? "Excellent"
      : score >= 60
        ? "Good"
        : score >= 40
          ? "Fair"
          : "Poor";
  const scoreColor =
    score >= 80
      ? "text-emerald-500"
      : score >= 60
        ? "text-chart-1"
        : score >= 40
          ? "text-amber-500"
          : "text-destructive";
  const scoreBadgeBg =
    score >= 80
      ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30"
      : score >= 60
        ? "bg-blue-500/10 text-blue-600 border-blue-500/30"
        : score >= 40
          ? "bg-amber-500/10 text-amber-600 border-amber-500/30"
          : "bg-destructive/10 text-destructive border-destructive/30";
  const heroGradient =
    score >= 80
      ? "from-emerald-500/20 via-emerald-400/10 to-secondary/5"
      : score >= 60
        ? "from-primary/20 via-primary/10 to-accent/5"
        : score >= 40
          ? "from-amber-500/20 via-amber-400/10 to-accent/5"
          : "from-destructive/20 via-destructive/10 to-accent/5";
  const heroBorder =
    score >= 80
      ? "border-emerald-500/40"
      : score >= 60
        ? "border-primary/40"
        : score >= 40
          ? "border-amber-500/40"
          : "border-destructive/40";
  const heroIconBg =
    score >= 80
      ? "bg-emerald-500"
      : score >= 60
        ? "bg-primary"
        : score >= 40
          ? "bg-amber-500"
          : "bg-destructive";

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="sticky top-0 z-20 border-b border-border bg-background">
        <div className="flex items-center justify-between p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/camera")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-bold">Review Meal</h1>
          <div className="w-10" />
        </div>
      </div>

      <div className="space-y-4 p-4">
        {/* Image Preview */}
        <Card className="relative overflow-hidden">
          <div className="flex aspect-video items-center justify-center bg-muted">
            <Camera className="h-16 w-16 text-muted-foreground" />
          </div>
          <div className="absolute right-2 top-2">
            <Badge
              variant="secondary"
              className="bg-background/80 backdrop-blur"
            >
              {foodItems.length} items detected
            </Badge>
          </div>
        </Card>

        {/* ===== GLUCOSE SPIKE PREDICTOR ===== */}
        {foodItems.length > 0 &&
          (() => {
            const predictedSpike = 38.2;
            const spikeCategory =
              avgGL >= 15 ? "High" : avgGL >= 8 ? "Medium" : "Low";
            const confidence: string = "moderate";
            const timeToPeak = 45;
            const recoveryTime = 120;
            const spikeDesc =
              spikeCategory === "High"
                ? "This meal may cause a significant blood sugar spike. Consider adding fiber or protein."
                : spikeCategory === "Medium"
                  ? "Moderate glucose impact expected. Balanced glycemic profile."
                  : "Minimal blood sugar impact. Great for stable energy levels.";

            const spikeColor =
              spikeCategory === "High"
                ? "text-destructive"
                : spikeCategory === "Medium"
                  ? "text-amber-500"
                  : "text-emerald-500";
            const spikeBg =
              spikeCategory === "High"
                ? "bg-destructive/10 border-destructive/30"
                : spikeCategory === "Medium"
                  ? "bg-amber-500/10 border-amber-500/30"
                  : "bg-emerald-500/10 border-emerald-500/30";
            const spikeIconBg =
              spikeCategory === "High"
                ? "bg-destructive/20"
                : spikeCategory === "Medium"
                  ? "bg-amber-500/20"
                  : "bg-emerald-500/20";
            const spikeBadgeBg =
              spikeCategory === "High"
                ? "bg-destructive/15 text-destructive border-destructive/40"
                : spikeCategory === "Medium"
                  ? "bg-amber-500/15 text-amber-600 border-amber-500/40"
                  : "bg-emerald-500/15 text-emerald-600 border-emerald-500/40";
            const confidenceColor =
              confidence === "high"
                ? "text-emerald-500"
                : confidence === "moderate"
                  ? "text-amber-500"
                  : "text-muted-foreground";
            const confidenceLabel =
              confidence.charAt(0).toUpperCase() + confidence.slice(1);

            return (
              <Card className={`border p-4 ${spikeBg} space-y-3`}>
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-lg ${spikeIconBg}`}
                  >
                    <Zap className={`h-5 w-5 ${spikeColor}`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-muted-foreground">
                      Predicted Glucose Spike
                    </p>
                    <span className={`text-sm font-semibold ${spikeColor}`}>
                      +{predictedSpike} mg/dL
                    </span>
                  </div>
                  <div
                    className={`rounded-full border px-3 py-1.5 text-sm font-bold ${spikeBadgeBg}`}
                  >
                    {spikeCategory === "High"
                      ? "⚠ High"
                      : spikeCategory === "Medium"
                        ? "⚡ Moderate"
                        : "✓ Low"}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-lg bg-background/60 p-2.5 text-center">
                    <div className="mb-1 flex items-center justify-center gap-1">
                      <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-bold text-foreground">
                      {timeToPeak} min
                    </p>
                    <p className="text-[10px] font-medium text-muted-foreground">
                      Time to Peak
                    </p>
                  </div>
                  <div className="rounded-lg bg-background/60 p-2.5 text-center">
                    <div className="mb-1 flex items-center justify-center gap-1">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-bold text-foreground">
                      {recoveryTime} min
                    </p>
                    <p className="text-[10px] font-medium text-muted-foreground">
                      Recovery Time
                    </p>
                  </div>
                  <div className="rounded-lg bg-background/60 p-2.5 text-center">
                    <div className="mb-1 flex items-center justify-center gap-1">
                      <Shield className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <p className={`text-sm font-bold ${confidenceColor}`}>
                      {confidenceLabel}
                    </p>
                    <p className="text-[10px] font-medium text-muted-foreground">
                      Confidence
                    </p>
                  </div>
                </div>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {spikeDesc}
                </p>
              </Card>
            );
          })()}

        {/* ===== TOTAL NUTRITION ===== */}
        <Card className="border-2 p-4">
          <h2 className="mb-4 text-lg font-bold">Total Nutrition</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-muted-foreground">
                Calories
              </p>
              <p className="text-2xl font-bold text-primary">
                {totalNutrition.calories}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-muted-foreground">
                Protein
              </p>
              <p className="text-chart-1 text-2xl font-bold">
                {totalNutrition.protein}g
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-muted-foreground">
                Carbs
              </p>
              <p className="text-chart-2 text-2xl font-bold">
                {totalNutrition.carbs}g
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-muted-foreground">Fat</p>
              <p className="text-chart-3 text-2xl font-bold">
                {totalNutrition.fat}g
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-muted-foreground">
                Sugar
              </p>
              <p className="text-chart-4 text-2xl font-bold">
                {totalNutrition.sugar}g
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-muted-foreground">
                Fiber
              </p>
              <p className="text-chart-5 text-2xl font-bold">
                {totalNutrition.fiber}g
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-muted-foreground">
                GI (avg)
              </p>
              <p className="text-2xl font-bold text-accent">{avgGI}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-muted-foreground">
                GL (avg)
              </p>
              <p className="text-info text-2xl font-bold">{avgGL}</p>
            </div>
          </div>
        </Card>

        {/* ===== FOOD ITEMS ===== */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">Food Items</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/meals/add-item")}
            >
              <Plus className="mr-1 h-4 w-4" />
              Add Item
            </Button>
          </div>

          {foodItems.map(item => (
            <Card
              key={item.id}
              className="overflow-hidden border-2 transition-all"
            >
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold">{item.name}</h3>
                    <p className="mb-1 text-sm text-muted-foreground">
                      {item.servingSize}
                    </p>
                    <span className="font-bold text-primary">
                      {item.calories} cal
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={e => {
                        e.stopPropagation();
                        navigate(`/meals/edit-item/${item.id}`, {
                          state: { item },
                        });
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteItem(item.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() =>
                        setExpandedItem(
                          expandedItem === item.id ? null : item.id
                        )
                      }
                    >
                      {expandedItem === item.id ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {expandedItem === item.id && (
                <div className="space-y-3 border-t border-border bg-muted/30 px-4 pb-4 pt-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center justify-between border-b border-border pb-2">
                      <span className="text-sm font-semibold">Calories</span>
                      <span className="font-bold text-primary">
                        {item.calories} kcal
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-border pb-2">
                      <span className="text-sm font-semibold">Protein</span>
                      <span className="text-chart-1 font-bold">
                        {item.protein}g
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-border pb-2">
                      <span className="text-sm font-semibold">Total Carbs</span>
                      <span className="text-chart-2 font-bold">
                        {item.carbs}g
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-border pb-2">
                      <span className="text-sm font-semibold">Net Carbs</span>
                      <span className="text-chart-2 font-bold">
                        {item.netCarbs}g
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-border pb-2">
                      <span className="text-sm font-semibold">Fiber</span>
                      <span className="text-chart-5 font-bold">
                        {item.fiber}g
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-border pb-2">
                      <span className="text-sm font-semibold">Total Fat</span>
                      <span className="text-chart-3 font-bold">
                        {item.fat}g
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-border pb-2">
                      <span className="text-sm font-semibold">Trans Fat</span>
                      <span className="text-chart-3 font-bold">
                        {item.transFat}g
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-border pb-2">
                      <span className="text-sm font-semibold">
                        Saturated Fat
                      </span>
                      <span className="text-chart-3 font-bold">
                        {item.saturatedFat}g
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-border pb-2">
                      <span className="text-sm font-semibold">Sugar</span>
                      <span className="text-chart-4 font-bold">
                        {item.sugar}g
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-border pb-2">
                      <span className="text-sm font-semibold">GI</span>
                      <span className="font-bold text-accent">{item.gi}</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-border pb-2">
                      <span className="text-sm font-semibold">GL</span>
                      <span className="text-info font-bold">{item.gl}</span>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>

        {/* ===== MEAL SCORE + INSIGHT (just before Save) ===== */}
        {foodItems.length > 0 && (
          <div
            className={`relative overflow-hidden rounded-2xl border-2 ${heroBorder} shadow-lg`}
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${heroGradient}`}
            />
            <div className="absolute right-0 top-0 h-32 w-32 rounded-bl-full bg-gradient-to-bl from-primary/10 to-transparent" />

            <div className="relative space-y-4 p-5">
              {/* Score + Label Row */}
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-background/50 bg-background/80 shadow-md backdrop-blur">
                  <span className={`text-2xl font-black ${scoreColor}`}>
                    {score}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Meal Score
                  </p>
                  <Badge
                    className={`${scoreBadgeBg} mt-1 border px-3 py-0.5 text-sm`}
                  >
                    {scoreLabel}
                  </Badge>
                </div>
                <div
                  className={`h-9 w-9 rounded-xl ${heroIconBg} flex items-center justify-center shadow-md`}
                >
                  <Sparkles className="w-4.5 h-4.5 text-primary-foreground" />
                </div>
              </div>

              {/* Progress Bar */}
              <div className="flex items-center gap-2">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted/60">
                  <div
                    className={`h-full rounded-full ${heroIconBg} transition-all duration-700`}
                    style={{ width: `${score}%` }}
                  />
                </div>
                <span className={`text-xs font-bold ${scoreColor}`}>
                  {score}/100
                </span>
              </div>

              {/* Insight */}
              <div className="rounded-xl bg-background/40 p-3.5 backdrop-blur">
                <div className="flex items-start gap-2.5">
                  <Sparkles
                    className={`mt-0.5 h-4 w-4 flex-shrink-0 ${scoreColor}`}
                  />
                  <p className="text-sm font-semibold leading-relaxed text-foreground">
                    {insight}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== SAVE MEAL ===== */}
        <Card className="space-y-4 p-4">
          <div className="space-y-2">
            <label className="text-sm font-bold">Save as</label>
            <Select value={mealType} onValueChange={setMealType}>
              <SelectTrigger className="border-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="breakfast">Breakfast</SelectItem>
                <SelectItem value="lunch">Lunch</SelectItem>
                <SelectItem value="snack">Snack</SelectItem>
                <SelectItem value="dinner">Dinner</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button className="w-full" size="lg" onClick={handleSaveMeal}>
            Save Meal
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default MealPrediction;
